import { appRoutes } from "@/app/router/appRoutes";
import { Maybe } from "@/domains/common/types";
import { MDSButton, MDSButtonVariant, MDSSpacer } from "@/mds";
import {
  CommercialUsageRecoveryAction,
  type CommercialUsageLimitRejection,
} from "@/modules/commercial-plans";
import { UNTITLED_NOTE_TITLE } from "@/domains/untitled/untitled";
import { uuidModule } from "@/modules/uuid";
import { resolveNoteContentDocumentSyncModelUuid } from "@/modules/uuid/sync-models/resolveNoteContentDocumentSyncModelUuid";
import { resolveSpaceAccountNoteSyncModelUuid } from "@/modules/uuid/sync-models/resolveSpaceAccountNoteSyncModelUuid";
import { AppStore } from "@/store";
import { INoteObservable } from "@/store/note";
import { NoteIndexes } from "@/store/note/NoteIndexes";
import {
  IndexedNoteSyncUpdateValue,
  NoteContentDocumentModelData,
  NoteContentDocumentUpsertedSyncUpdateValue,
  NoteModelData,
  NoteUpsertedSyncUpdateValue,
} from "@/store/note/types";
import {
  SpaceAccountNoteModelData,
  SpaceAccountNoteUpsertedSyncUpdateValue,
} from "@/store/recent-items/types";
import { BaseSyncOperation } from "@/store/sync/operations/BaseSyncOperation";
import { AbstractSyncErrorModalFieldsGenerator } from "@/store/sync/operations/errors/SyncErrorModalFields";
import { generateDefaultOwnerScopes } from "@/store/sync/operations/helpers/common";
import { UpdateNoteContentUsingDiffOperation } from "@/store/sync/operations/notes/UpdateNoteContentUsingDiffOperation";
import { ICreateNoteOperation } from "@/store/sync/operations/types";
import {
  OptimisticUpdate,
  OptimisticSyncUpdate,
  SyncModelData,
  SyncModelUpsertedSyncUpdate,
  SyncOperationKind,
  SyncCustomErrorData,
} from "@/store/sync/types";

/**
 * Matches an empty tiptap JSON document.
 *
 * We use this instead of something like "" because it makes it possible to
 * merge the content with user-generated content (don't need extra checks for
 * empty string, ...)
 *
 * This also matches the default value for NoteContentDocument's encoded_content
 * on the backend.
 */
export const INITIAL_NOTE_CONTENT_DOCUMENT_ENCODED_CONTENT = "AAA=";

type OptimisticUpsertedSyncUpdate<ModelData extends SyncModelData> = OptimisticUpdate<
  SyncModelUpsertedSyncUpdate<ModelData>
>;

export class CreateNoteOperation extends BaseSyncOperation<ICreateNoteOperation> {
  get operationKind(): SyncOperationKind {
    return "CREATE_NOTE";
  }

  async generateOptimisticUpdates(): Promise<OptimisticSyncUpdate<SyncModelData>[]> {
    const { noteUpserted, noteContentDocumentUpserted, spaceAccountNoteUpserted } =
      this.buildOptimisticUpdates();

    return [noteUpserted, noteContentDocumentUpserted, spaceAccountNoteUpserted];
  }

  async triggerRecompute() {
    await this.store.spaceAccountNotes.recompute(this.spaceAccountNoteId);
    await this.store.notes.recompute(this.payload.id);
    await this.store.noteContentDocuments.recompute(this.noteContentDocumentId);
  }

  /**
   * Makes a new blank note renderable before the sync operation writes to IndexedDB.
   *
   * The normal execute path still owns persistence and queueing. This primes only
   * the in-memory model pools that the editor route reads while the create action
   * continues in the background.
   */
  public primeOptimisticModelsForEditor() {
    const { noteUpserted, noteContentDocumentUpserted, spaceAccountNoteUpserted } =
      this.buildOptimisticUpdates();

    const noteWithIndexes: IndexedNoteSyncUpdateValue = {
      ...noteUpserted.value,
      ...new NoteIndexes({
        store: this.store,
        remoteData: noteUpserted.value,
        optimisticUpdates: [noteUpserted],
        spaceAccountNote: spaceAccountNoteUpserted.value,
      }).indexes,
    };

    const note = this.store.notes.createSyncModel(noteWithIndexes);
    this.store.notes.pool.set(note.id, new WeakRef(note));

    const noteContentDocument = this.store.noteContentDocuments.createSyncModel(
      noteContentDocumentUpserted.value
    );
    this.store.noteContentDocuments.pool.set(
      noteContentDocument.id,
      new WeakRef(noteContentDocument)
    );
    note.setNoteContentDocument(noteContentDocument);

    const spaceAccountNote = this.store.spaceAccountNotes.createSyncModel(
      spaceAccountNoteUpserted.value
    );
    this.store.spaceAccountNotes.pool.set(spaceAccountNote.id, new WeakRef(spaceAccountNote));
  }

  private buildOptimisticUpdates() {
    const noteValue: NoteUpsertedSyncUpdateValue = {
      model_id: this.payload.id,
      model_kind: "NOTE",
      model_version: 0,
      model_data: {
        trashed_at: null,
        created_at: this.committedAt,
        quota_created_at: this.committedAt,
        locally_created_at: this.committedAt,
        locally_modified_at: this.committedAt,
        owned_by_space_account_id: this.store.spaceAccounts.myPersonalSpaceAccountId,
        created_by_space_account_id: this.store.spaceAccounts.myPersonalSpaceAccountId,
        attributed_created_by_space_account_id: null,
        modified_by_space_account_ids: [this.store.spaceAccounts.myPersonalSpaceAccountId],
        is_editable_by_ai: true,
        primary_label: "",
        primary_label_strategy: "INFERRED_FROM_CONTENT",
        secondary_label: "",
        secondary_label_strategy: "INFERRED_FROM_CONTENT",
        media_kinds: [],
        mentioned_note_ids: [],
        is_empty: true,
      },
      model_scopes: [generateDefaultOwnerScopes({ store: this.store })],
    };
    const noteUpserted: OptimisticUpsertedSyncUpdate<NoteModelData> = {
      optimistic_update_id: uuidModule.generate(),

      locally_committed_at: this.committedAt,
      kind: "UPSERTED",
      value: noteValue,
    };

    const noteContentDocumentId = this.noteContentDocumentId;
    const noteContentDocumentValue: NoteContentDocumentUpsertedSyncUpdateValue = {
      model_id: noteContentDocumentId,
      model_kind: "NOTE_CONTENT_DOCUMENT",
      model_version: 0,
      model_data: {
        note_id: this.payload.id,
        encoded_content: INITIAL_NOTE_CONTENT_DOCUMENT_ENCODED_CONTENT,
      },
      model_scopes: [generateDefaultOwnerScopes({ store: this.store })],
    };
    const noteContentDocumentUpserted: OptimisticUpsertedSyncUpdate<NoteContentDocumentModelData> =
      {
        optimistic_update_id: uuidModule.generate(),

        locally_committed_at: this.committedAt,
        kind: "UPSERTED",
        value: noteContentDocumentValue,
      };

    const spaceAccountId = this.store.spaceAccounts.myPersonalSpaceAccountId;
    const spaceAccountNoteValue: SpaceAccountNoteUpsertedSyncUpdateValue = {
      model_id: this.spaceAccountNoteId,
      model_kind: "SPACE_ACCOUNT_NOTE",
      model_version: 0,
      model_data: {
        space_account_id: spaceAccountId,
        note_id: this.payload.id,
        last_viewed_at: null,
        last_relevant_note_extraction_started_at: null,
        last_relevant_note_extraction_completed_at: null,
        is_running_relevant_notes_extraction_pipeline: false,
        last_relevant_note_extraction_note_model_version: null,
        shared_with_me_at: null,
        shared_with_me_by_space_account_id: null,
        suggestion_data: null,
        reminder_shill: null,
      },
      model_scopes: [generateDefaultOwnerScopes({ store: this.store })],
    };
    const spaceAccountNoteUpserted: OptimisticUpsertedSyncUpdate<SpaceAccountNoteModelData> = {
      optimistic_update_id: uuidModule.generate(),

      locally_committed_at: this.committedAt,
      kind: "UPSERTED",
      value: spaceAccountNoteValue,
    };

    return { noteUpserted, noteContentDocumentUpserted, spaceAccountNoteUpserted };
  }

  private get noteContentDocumentId() {
    return resolveNoteContentDocumentSyncModelUuid({
      noteId: this.payload.id,
    });
  }

  private get spaceAccountNoteId() {
    return resolveSpaceAccountNoteSyncModelUuid({
      spaceAccountId: this.store.spaceAccounts.myPersonalSpaceAccountId,
      noteId: this.payload.id,
    });
  }

  handleInvalidError(_errorData: SyncCustomErrorData) {
    this.triggerModal(this.getSyncErrorModalFieldsGenerator("INVALID"));
  }

  handlePermissionDeniedError(_errorData: SyncCustomErrorData) {
    this.triggerModal(this.getSyncErrorModalFieldsGenerator("PERMISSION_DENIED"));
  }

  get syncErrorModalFieldsGenerator() {
    return this.getSyncErrorModalFieldsGenerator("UNKNOWN");
  }

  getCommercialUsageLimitSyncErrorModalFieldsGenerator(rejection: CommercialUsageLimitRejection) {
    return (store: AppStore) =>
      new CreateNoteCommercialUsageLimitSyncErrorModalFieldsGenerator(
        store,
        this.payload.id,
        rejection
      );
  }

  private getSyncErrorModalFieldsGenerator(kind: SyncCustomErrorData["kind"]) {
    return (store: AppStore) => {
      const operations = this.store.sync.actionQueue.operationsByModelId.get(this.payload.id);
      const maybePendingUpdateOperation = operations?.find(
        e => e.operationKind === "UPDATE_NOTE_CONTENT_USING_DIFF" && !e.acknowledged
      ) as Maybe<UpdateNoteContentUsingDiffOperation>;

      if (!maybePendingUpdateOperation) {
        return new CreateNoteSyncErrorModalFieldsGenerator(store, this.payload.id);
      }

      return maybePendingUpdateOperation?.getSyncErrorModalFieldsGenerator(kind, {
        requestAccessAndSaveEditsInNewNoteButtonsEnabled: true,
      })(store);
    };
  }
}

/**
 * Keeps an edited, server-rejected note in the durable optimistic queue until
 * the person explicitly retries or discards it.
 *
 * The shared plan selector can cover this modal while the draft remains in the
 * paused queue, then return the person to the same recovery controls.
 */
class CreateNoteCommercialUsageLimitSyncErrorModalFieldsGenerator extends AbstractSyncErrorModalFieldsGenerator {
  constructor(
    store: AppStore,
    private readonly noteId: string,
    private readonly rejection: CommercialUsageLimitRejection
  ) {
    super(store);
  }

  get title() {
    return "Your note is preserved as a local draft";
  }

  get message() {
    return (
      <>
        You reached your new-note limit before this note could sync. Your text is still saved on
        this device. Review your plan or extra usage, then try again to create it in Mem.
      </>
    );
  }

  get resetActionLabel() {
    return null;
  }

  get hideWhileSettingsAreOpen() {
    return true;
  }

  get hideWhileUpsellIsOpen() {
    return true;
  }

  private discardDraft = async () => {
    await this.store.sync.actionQueue.skipAndRevertUnsyncedOperationsForModelId(this.noteId);
    this.store.navigation.goUpIfActivePathIs(
      appRoutes.notesView({ params: { noteId: this.noteId } }).path
    );
  };

  private reviewPlanAndExtraUsage = () => {
    this.store.subscriptions.openCommercialUsageLimit(this.rejection);
  };

  private tryAgain = () => {
    this.store.sync.actionQueue.resume();
  };

  get extraActionButtons() {
    const reviewLabel = this.rejection.recoveryActions.includes(
      CommercialUsageRecoveryAction.ReviewExtraUsage
    )
      ? "Review plans and extra usage"
      : "Compare plans";

    return (
      <>
        <MDSButton onClick={this.discardDraft}>Discard draft</MDSButton>
        <MDSSpacer />
        <MDSButton onClick={this.reviewPlanAndExtraUsage}>{reviewLabel}</MDSButton>
        <MDSButton variant={MDSButtonVariant.Contrast} onClick={this.tryAgain}>
          Try again
        </MDSButton>
      </>
    );
  }
}

class CreateNoteSyncErrorModalFieldsGenerator extends AbstractSyncErrorModalFieldsGenerator {
  protected noteId: string;
  protected note?: INoteObservable;

  constructor(store: AppStore, noteId: string) {
    super(store);

    this.noteId = noteId;
    this.note = this.store.notes.get(noteId);
  }

  get title() {
    return `“${this.note?.title || UNTITLED_NOTE_TITLE}” could not be created`;
  }

  get message() {
    return `The note will be removed from your Mem`;
  }

  modalActionHandler = async () => {
    this.store.sync.actionQueue.skipAndRevertUnsyncedOperationsForModelId(this.noteId);
    this.store.navigation.goUpIfActivePathIs(
      appRoutes.notesView({ params: { noteId: this.noteId } }).path
    );
  };
}
